/**
 * Enum para los roles de usuario
 */
export enum Role {
  ADMIN = 'ADMIN',
  RESPONSABLE_CAPTACION = 'RESPONSABLE_CAPTACION',
  ENTRENADOR = 'ENTRENADOR'
}

/**
 * Enum para las secciones
 */
export enum Seccion {
  F7_MASCULINO = 'F7_MASCULINO',
  F11_MASCULINO = 'F11_MASCULINO',
  F7_FEMENINO = 'F7_FEMENINO',
  F11_FEMENINO = 'F11_FEMENINO'
}

/**
 * Enum para las categorías
 */
export enum Categoria {
  DIVISION_HONOR = 'DIVISION_HONOR',
  NACIONAL = 'NACIONAL',
  PREFERENTE = 'PREFERENTE',
  PRIMERA = 'PRIMERA',
  SEGUNDA = 'SEGUNDA',
  TERCERA = 'TERCERA',
  PREBENJAMIN = 'PREBENJAMIN',
  BENJAMIN_FEM = 'BENJAMIN_FEM',
  TERCERA_FEDERACION_FEM = 'TERCERA_FEDERACION_FEM',
  SEGUNDA_FEDERACION_FEM = 'SEGUNDA_FEDERACION_FEM',
  PRIMERA_FEDERACION_FEM = 'PRIMERA_FEDERACION_FEM'
}

/**
 * Enum para las competiciones
 */
export enum Competicion {
  S7 = 'S7',
  S8 = 'S8',
  S9 = 'S9',
  S10 = 'S10',
  S11 = 'S11',
  S12 = 'S12',
  S13 = 'S13',
  S14 = 'S14',
  S15 = 'S15',
  S16 = 'S16',
  JUVENIL = 'JUVENIL',
  BENJAMI_FEM = 'BENJAMI_FEM',
  ALEVI_FEM = 'ALEVI_FEM',
  INFANTIL_FEM = 'INFANTIL_FEM',
  CADET_FEM = 'CADET_FEM',
  JUVENIL_FEM = 'JUVENIL_FEM',
  AMATEUR_FEM = 'AMATEUR_FEM'
}

/**
 * Obtiene todos los roles disponibles
 * @returns Array con todos los roles
 */
export const getRoles = (): Role[] => {
  return Object.values(Role);
};

/**
 * Obtiene el label legible de un rol
 * @param role - El rol a convertir
 * @returns El label legible del rol
 */
export const getRoleLabel = (role: Role): string => {
  const labels: Record<Role, string> = {
    [Role.ADMIN]: 'Administrador',
    [Role.RESPONSABLE_CAPTACION]: 'Responsable de Captación',
    [Role.ENTRENADOR]: 'Entrenador'
  };
  return labels[role];
};

/**
 * Obtiene todas las categorías disponibles filtradas por sección
 * @param seccion - La sección del usuario (opcional)
 * @returns Array con las categorías filtradas
 */
export const getCategorias = (seccion?: Seccion): Categoria[] => {
  let categorias = Object.values(Categoria);

  if (seccion) {
    if (seccion === Seccion.F11_MASCULINO) {
      // Si es F11 masculino: Division de honor, preferente, primera y segunda
      const permitidas = [
        Categoria.DIVISION_HONOR,
        Categoria.PREFERENTE,
        Categoria.PRIMERA,
        Categoria.SEGUNDA
      ];
      categorias = categorias.filter(c => permitidas.includes(c));
    } else if (seccion === Seccion.F11_FEMENINO) {
      // Si es F11 femenino: preferente, primera, segunda, y todas las femeninas
      const permitidas = [
        Categoria.PREFERENTE,
        Categoria.PRIMERA,
        Categoria.SEGUNDA,
        Categoria.TERCERA_FEDERACION_FEM,
        Categoria.SEGUNDA_FEDERACION_FEM,
        Categoria.PRIMERA_FEDERACION_FEM
      ];
      categorias = categorias.filter(c => permitidas.includes(c));
    } else if (seccion === Seccion.F7_MASCULINO) {
      // Si es F7 (Masculino o Femenino): Preferente, Primera, Segunda, Tercera y Prebenjamín
      const permitidas = [
        Categoria.PREFERENTE,
        Categoria.PRIMERA,
        Categoria.SEGUNDA,
        Categoria.TERCERA,
        Categoria.PREBENJAMIN
      ];
      categorias = categorias.filter(c => permitidas.includes(c));
    } else if (seccion === Seccion.F7_FEMENINO) {
      // Si es F7 (Masculino o Femenino): Preferente, Primera, Segunda, Tercera y Prebenjamín
      const permitidas = [
        Categoria.PREFERENTE,
        Categoria.PRIMERA,
        Categoria.SEGUNDA,
        Categoria.TERCERA,
        Categoria.PREBENJAMIN,
        Categoria.BENJAMIN_FEM
      ];
      categorias = categorias.filter(c => permitidas.includes(c));
    }
  }

  return categorias;
};

/**
 * Obtiene el label legible de una categoría
 * @param categoria - La categoría a convertir
 * @returns El label legible de la categoría
 */
export const getCategoriaLabel = (categoria: Categoria): string => {
  const labels: Record<Categoria, string> = {
    [Categoria.DIVISION_HONOR]: 'División de Honor',
    [Categoria.NACIONAL]: 'Nacional',
    [Categoria.TERCERA_FEDERACION_FEM]: 'Tercera Federación Fem',
    [Categoria.SEGUNDA_FEDERACION_FEM]: 'Segunda Federación Fem',
    [Categoria.PRIMERA_FEDERACION_FEM]: 'Primera Federación Fem',
    [Categoria.PREFERENTE]: 'Preferente',
    [Categoria.PRIMERA]: 'Primera',
    [Categoria.SEGUNDA]: 'Segunda',
    [Categoria.TERCERA]: 'Tercera',
    [Categoria.PREBENJAMIN]: 'Prebenjamín',
    [Categoria.BENJAMIN_FEM]: 'Benjamín Femenino'
  };
  return labels[categoria];
};

/**
 * Obtiene todas las competiciones disponibles filtradas por sección
 * @param seccion - La sección del usuario (opcional)
 * @returns Array con todas las competiciones filtradas
 */
export const getCompeticiones = (seccion?: Seccion): Competicion[] => {
  let competiciones = Object.values(Competicion);

  if (seccion) {
    if (seccion === Seccion.F11_MASCULINO) {
      // F11 Masculino: S12 a S16 y Juvenil
      const permitidas = [
        Competicion.S12,
        Competicion.S13,
        Competicion.S14,
        Competicion.S15,
        Competicion.S16,
        Competicion.JUVENIL
      ];
      competiciones = competiciones.filter(c => permitidas.includes(c));
    } else if (seccion === Seccion.F7_MASCULINO) {
      // F7 Masculino: S7 a S12
      const permitidas = [
        Competicion.S7,
        Competicion.S8,
        Competicion.S9,
        Competicion.S10,
        Competicion.S11,
        Competicion.S12
      ];
      competiciones = competiciones.filter(c => permitidas.includes(c));
    } else if (seccion === Seccion.F7_FEMENINO) {
      // F7 Femenino: Benjami_FEM, Alevi_FEM e Infantil_FEM
      const permitidas = [
        Competicion.BENJAMI_FEM,
        Competicion.ALEVI_FEM,
        Competicion.INFANTIL_FEM
      ];
      competiciones = competiciones.filter(c => permitidas.includes(c));
    } else if (seccion === Seccion.F11_FEMENINO) {
      // F11 Femenino: Alevi_FEM hasta Amateur_FEM
      const permitidas = [
        Competicion.ALEVI_FEM,
        Competicion.INFANTIL_FEM,
        Competicion.CADET_FEM,
        Competicion.JUVENIL_FEM,
        Competicion.AMATEUR_FEM
      ];
      competiciones = competiciones.filter(c => permitidas.includes(c));
    }
  }

  return competiciones;
};

/**
 * Obtiene el label legible de una competición
 * @param competicion - La competición a convertir
 * @returns El label legible de la competición
 */
export const getCompeticionLabel = (competicion: Competicion): string => {
  const labels: Record<Competicion, string> = {
    [Competicion.S7]: 'S7',
    [Competicion.S8]: 'S8',
    [Competicion.S9]: 'S9',
    [Competicion.S10]: 'S10',
    [Competicion.S11]: 'S11',
    [Competicion.S12]: 'S12',
    [Competicion.S13]: 'S13',
    [Competicion.S14]: 'S14',
    [Competicion.S15]: 'S15',
    [Competicion.S16]: 'S16',
    [Competicion.JUVENIL]: 'Juvenil',
    [Competicion.JUVENIL_FEM]: 'Juvenil Femenino',
    [Competicion.AMATEUR_FEM]: 'Amateur Femenino',
    [Competicion.INFANTIL_FEM]: 'Infantil Femenino',
    [Competicion.CADET_FEM]: 'Cadete Femenino',
    [Competicion.ALEVI_FEM]: 'Alevi Femenino',
    [Competicion.BENJAMI_FEM]: 'Benjami Femenino'
  };
  return labels[competicion];
};

/**
 * Enum para la lateralidad de un jugador
 */
export enum Lateralidad {
  DIESTRO = 'DIESTRO',
  ZURDO = 'ZURDO',
  AMBIDIESTRO = 'AMBIDIESTRO'
}

/**
 * Enum para las categorías futuras de jugadores
 */
export enum CategoriaFutura {
  DIVISION_HONOR = 'DIVISION_HONOR',
  NACIONAL = 'NACIONAL',
  PREFERENTE = 'PREFERENTE',
  PRIMERA = 'PRIMERA',
  SEGUNDA = 'SEGUNDA',
  TERCERA_FEDERACION_FEM = 'TERCERA_FEDERACION_FEM',
  SEGUNDA_FEDERACION_FEM = 'SEGUNDA_FEDERACION_FEM',
  PRIMERA_FEDERACION_FEM = 'PRIMERA_FEDERACION_FEM'
}

/**
 * Enum para el nivel de evaluación de un jugador
 */
export enum Nivel {
  FICHAR = 'FICHAR',
  INTERESANTE = 'INTERESANTE',
  SEGUIR = 'SEGUIR',
  DESCARTAR = 'DESCARTAR'
}

/**
 * Enum para las posiciones de juego
 */
export enum Posicion {
  PORTERO = 'PORTERO',
  CENTRAL = 'CENTRAL',
  CENTRAL_DIESTRO = 'CENTRAL_DIESTRO',
  CENTRAL_ZURDO = 'CENTRAL_ZURDO',
  LAT_IZQ = 'LAT_IZQ',
  LAT_DER = 'LAT_DER',
  PIVOTE = 'PIVOTE',
  MEDIAPUNTA = 'MEDIAPUNTA',
  EXT_IZQ = 'EXT_IZQ',
  EXT_DER = 'EXT_DER',
  DELANTERO = 'DELANTERO',
}

/**
 * Enum para las letras de equipo
 */
export enum LetraEquipo {
  A = 'A', B = 'B', C = 'C', D = 'D', E = 'E', F = 'F', G = 'G', H = 'H',
  I = 'I', J = 'J', K = 'K', L = 'L', M = 'M', N = 'N', O = 'O', P = 'P',
  Q = 'Q', R = 'R', S = 'S', T = 'T', U = 'U', V = 'V', W = 'W', X = 'X',
  Y = 'Y', Z = 'Z'
}

/**
 * Obtiene todas las lateralidades disponibles
 * @returns Array con todas las lateralidades
 */
export const getLateralidades = (): Lateralidad[] => {
  return Object.values(Lateralidad);
};

/**
 * Obtiene el label legible de una lateralidad
 * @param lateralidad - La lateralidad a convertir
 * @returns El label legible de la lateralidad
 */
export const getLateralidadLabel = (lateralidad: Lateralidad): string => {
  const labels: Record<Lateralidad, string> = {
    [Lateralidad.DIESTRO]: 'Diestro',
    [Lateralidad.ZURDO]: 'Zurdo',
    [Lateralidad.AMBIDIESTRO]: 'Ambidiestro'
  };
  return labels[lateralidad];
};

/**
 * Obtiene todas las categorías futuras disponibles filtradas por sección
 * @param seccion - La sección del usuario (opcional)
 * @returns Array con las categorías futuras filtradas
 */
export const getCategoriasFuturas = (seccion?: Seccion): CategoriaFutura[] => {
  let categoriasFuturas = Object.values(CategoriaFutura);

  if (seccion) {
    if (seccion === Seccion.F11_MASCULINO) {
      // Si es F11 masculino: Division de honor, preferente, primera y segunda
      const permitidas = [
        CategoriaFutura.DIVISION_HONOR,
        CategoriaFutura.PREFERENTE,
        CategoriaFutura.PRIMERA,
        CategoriaFutura.SEGUNDA
      ];
      categoriasFuturas = categoriasFuturas.filter(c => permitidas.includes(c));
    } else if (seccion === Seccion.F11_FEMENINO) {
      // Si es F11 femenino: preferente, primera, segunda, y todas las femeninas
      const permitidas = [
        CategoriaFutura.PREFERENTE,
        CategoriaFutura.PRIMERA,
        CategoriaFutura.SEGUNDA,
        CategoriaFutura.TERCERA_FEDERACION_FEM,
        CategoriaFutura.SEGUNDA_FEDERACION_FEM,
        CategoriaFutura.PRIMERA_FEDERACION_FEM
      ];
      categoriasFuturas = categoriasFuturas.filter(c => permitidas.includes(c));
    } else if (seccion === Seccion.F7_MASCULINO || seccion === Seccion.F7_FEMENINO) {
      // Si es F7: Preferente, Primera y Segunda
      const permitidas = [
        CategoriaFutura.PREFERENTE,
        CategoriaFutura.PRIMERA,
        CategoriaFutura.SEGUNDA
      ];
      categoriasFuturas = categoriasFuturas.filter(c => permitidas.includes(c));
    }
  }

  return categoriasFuturas;
};

/**
 * Obtiene el label legible de una categoría futura
 * @param categoriaFutura - La categoría futura a convertir
 * @returns El label legible de la categoría futura
 */
export const getCategoriaFuturaLabel = (categoriaFutura: CategoriaFutura): string => {
  const labels: Record<CategoriaFutura, string> = {
    [CategoriaFutura.DIVISION_HONOR]: 'División de Honor',
    [CategoriaFutura.PREFERENTE]: 'Preferente',
    [CategoriaFutura.PRIMERA]: 'Primera',
    [CategoriaFutura.SEGUNDA]: 'Segunda',
    [CategoriaFutura.NACIONAL]: 'Nacional',
    [CategoriaFutura.TERCERA_FEDERACION_FEM]: 'Tercera Federación Fem',
    [CategoriaFutura.SEGUNDA_FEDERACION_FEM]: 'Segunda Federación Fem',
    [CategoriaFutura.PRIMERA_FEDERACION_FEM]: 'Primera Federación Fem'
  };
  return labels[categoriaFutura];
};

/**
 * Obtiene todos los niveles disponibles
 * @returns Array con todos los niveles
 */
export const getNiveles = (): Nivel[] => {
  return Object.values(Nivel);
};

/**
 * Obtiene el label legible de un nivel
 * @param nivel - El nivel a convertir
 * @returns El label legible del nivel
 */
export const getNivelLabel = (nivel: Nivel): string => {
  const labels: Record<Nivel, string> = {
    [Nivel.FICHAR]: 'Fichar',
    [Nivel.INTERESANTE]: 'Interesante',
    [Nivel.SEGUIR]: 'Seguir',
    [Nivel.DESCARTAR]: 'Descartar'
  };
  return labels[nivel];
};

/**
 * Obtiene todas las posiciones disponibles filtradas por sección
 * @param seccion - La sección del usuario (opcional)
 * @returns Array con las posiciones filtradas
 */
export const getPosiciones = (seccion?: Seccion): Posicion[] => {
  let posiciones = Object.values(Posicion);

  if (seccion) {
    const esF11 = seccion.includes('F11');
    const esF7 = seccion.includes('F7');

    if (esF11) {
      // Si es F11 mostrará todas las posiciones excepto CENTRAL
      posiciones = posiciones.filter(p => p !== Posicion.CENTRAL);
    } else if (esF7) {
      // Si es F7 mostrará todas las posiciones excepto CENTRAL DIESTRO, CENTRAL IZQUIERDO, EXT. IZQ y EXT DER
      const excluidas = [
        Posicion.CENTRAL_DIESTRO,
        Posicion.CENTRAL_ZURDO,
        Posicion.EXT_IZQ,
        Posicion.EXT_DER
      ];
      posiciones = posiciones.filter(p => !excluidas.includes(p));
    }
  }

  return posiciones;
};

/**
 * Obtiene el label legible de una posición
 * @param posicion - La posición a convertir
 * @returns El label legible de la posición
 */
export const getPosicionLabel = (posicion: Posicion): string => {
  const labels: Record<Posicion, string> = {
    [Posicion.PORTERO]: 'PORTERO',
    [Posicion.CENTRAL]: 'CENTRAL',
    [Posicion.CENTRAL_DIESTRO]: 'CENTRAL DERECHO',
    [Posicion.CENTRAL_ZURDO]: 'CENTRAL IZQUIERDO',
    [Posicion.LAT_IZQ]: 'LAT. IZQ',
    [Posicion.LAT_DER]: 'LAT. DER',
    [Posicion.PIVOTE]: 'PIVOTE',
    [Posicion.MEDIAPUNTA]: 'MEDIAPUNTA',
    [Posicion.EXT_IZQ]: 'EXT. IZQ',
    [Posicion.EXT_DER]: 'EXT. DER',
    [Posicion.DELANTERO]: 'DELANTERO',
  };
  return labels[posicion];
};

/**
 * Obtiene todas las letras de equipo disponibles
 * @returns Array con todas las letras de equipo
 */
export const getLetrasEquipo = (): LetraEquipo[] => {
  return Object.values(LetraEquipo);
};

/**
 * Obtiene el label legible de una letra de equipo
 * @param letra - La letra de equipo a convertir
 * @returns El label legible de la letra de equipo
 */
export const getLetraEquipoLabel = (letra: LetraEquipo): string => {
  return `${letra}`;
};

/**
 * Obtiene los años disponibles para jugadores filtrados por sección
 * @param seccion - La sección del usuario (opcional)
 * @returns Array con los años disponibles
 */
export const getAñosDisponibles = (seccion?: Seccion): number[] => {
  const añoActual = new Date().getFullYear();
  const años: number[] = [];
  
  if (seccion && (seccion === Seccion.F11_MASCULINO || seccion === Seccion.F11_FEMENINO)) {
    // Si es F11: Desde -40 años hasta -12 años con el actual
    for (let i = añoActual - 40; i <= añoActual - 12; i++) {
      años.push(i);
    }
  } else if (seccion === Seccion.F7_FEMENINO) {
    // Si es F7 Femenino: Desde -13 años hasta -5 años con el actual
    for (let i = añoActual - 13; i <= añoActual - 5; i++) {
      años.push(i);
    }
  } else {
    // Si es F7 o defecto: Desde -11 años hasta -5 años con el actual
    for (let i = añoActual - 11; i <= añoActual - 5; i++) {
      años.push(i);
    }
  }
  
  return años;
};